import {reportPhotoLimits,type ReportAnswers} from '@myfm/domain';

export type PhotoTransferStage='saving-draft'|'uploading'|'attaching'|'reloading'|'complete';
type Receipt={photoId:string;version:number};
export type PhotoTransferPorts={
 save:(answers:ReportAnswers,version:number)=>Promise<{version:number}>;
 upload:(body:{questionId:string;fileName:string;contentBase64:string},version:number)=>Promise<Receipt>;
 reload:()=>Promise<unknown>;
 saved:(answers:ReportAnswers,version:number)=>void;
 stored:(receipt:Receipt)=>void;
 stage:(stage:PhotoTransferStage)=>void;
};
/** One open-tab transfer retains the exact payload, source version and receipt across retries. No durable offline store. */
export class ReportPhotoTransfer {
 private readonly file:File;
 readonly questionId:string;
 private answers:ReportAnswers;
 private version:number;
 private readonly ports:PhotoTransferPorts;
 private stage:PhotoTransferStage;
 private body:{questionId:string;fileName:string;contentBase64:string}|undefined;
 private receipt:Receipt|undefined;
 private attachment:ReportAnswers|undefined;
 constructor(file:File,questionId:string,answers:ReportAnswers,version:number,dirty:boolean,ports:PhotoTransferPorts){
  if(file.size>reportPhotoLimits.bytes)throw Error('Choose a photograph up to 10 MB.');
  if(!['image/jpeg','image/png','image/webp'].includes(file.type))throw Error('Choose JPEG, PNG or WebP. HEIC is not supported; use a supported camera format.');
  if((answers[questionId]?.photoIds?.length??0)>=reportPhotoLimits.attachedPerQuestion)throw Error('Attach up to 12 photographs to this question. Remove an attachment before adding another.');
  this.file=file;this.questionId=questionId;this.version=version;this.ports=ports;
  this.answers=structuredClone(answers);this.stage=dirty?'saving-draft':'uploading';
 }
 async resume(){
  this.ports.stage(this.stage);
  if(this.stage==='saving-draft'){
   const saved=await this.ports.save(this.answers,this.version);this.version=saved.version;this.ports.saved(this.answers,this.version);this.stage='uploading';this.ports.stage(this.stage);
  }
  if(this.stage==='uploading'){
   if(!this.body){
    const bytes=new Uint8Array(await this.file.arrayBuffer());let binary='';
    for(let i=0;i<bytes.length;i+=8192)binary+=String.fromCharCode(...bytes.subarray(i,i+8192));
    this.body={questionId:this.questionId,fileName:this.file.name,contentBase64:btoa(binary)};
   }
   this.receipt=await this.ports.upload(this.body,this.version);
   this.version=this.receipt.version;this.ports.stored(this.receipt);
   this.attachment={...this.answers,[this.questionId]:{...this.answers[this.questionId],photoIds:[...new Set([...(this.answers[this.questionId]?.photoIds??[]),this.receipt.photoId])]}};
   this.stage='attaching';this.ports.stage(this.stage);
  }
  if(this.stage==='attaching'){
   const saved=await this.ports.save(this.attachment!,this.version);this.version=saved.version;this.ports.saved(this.attachment!,this.version);
   this.stage='reloading';this.ports.stage(this.stage);
  }
  if(this.stage==='reloading'){
   await this.ports.reload();this.stage='complete';this.ports.stage(this.stage);
  }
 }
}
